import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  Calendar,
  CheckCircle,
  XCircle,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ArrowUp,
  ArrowDown,
} from 'react-bootstrap-icons'

// Assuming TmsHeader and TmsSidebar are defined elsewhere
import TmsHeader from '../TmsComponents/TmsHeader'
import TmsSidebar from '../TmsComponents/TmsSidebar'

// Mock data (you would typically fetch this from an API)
// leave as is for now

const TmsDashboard = () => {
  const [trainingStatusData, setTrainingStatusData] = useState([])
  // { status: "Declined", value: 5 },
  const [participantsData, setParticipantsData] = useState([])
  //{ training: "Leadership", value: 45 },
  const [monthlyTrainingData, setMonthlyTrainingData] = useState([])
  //{ month: "Jan", value: 20 },
  const [recentTrainings, setRecentTrainings] = useState([])
  //{ training: "Data Science Fundamentals", participants: 50, status: "Upcoming", date: "2023-07-10" },

  const [totalTrainings, setTotalTrainings] = useState(0)
  const [totalParticipants, setTotalParticipants] = useState(0)
  const [approvedTrainings, setApprovedTrainings] = useState(0)
  const [declinedTrainings, setDeclinedTrainings] = useState(0)

  const stats = [
    {
      label: 'Total Trainings',
      value: totalTrainings,
      icon: Calendar,
      color: 'warning',
      trend: 'up',
      percentage: '12%',
    },
    {
      label: 'Total Participants',
      value: totalParticipants,
      icon: CheckCircle,
      color: 'success',
      trend: 'up',
      percentage: '8%',
    },
    {
      label: 'Approved Trainings',
      value: approvedTrainings,
      icon: CheckCircle,
      color: 'primary',
      trend: 'up',
      percentage: '5%',
    },
    {
      label: 'Declined Trainings',
      value: declinedTrainings,
      icon: XCircle,
      color: 'danger',
      trend: 'down',
      percentage: '2%',
    },
  ]

  const COLORS = ['#00C49F', '#FF0000', '#FFBB28', '#0088FE']

  const URL = 'https://tdtlworld.com/dss-kaas-backend/'

  useEffect(() => {
    const fetchDashboard = async () => {
      const token = localStorage.getItem('jwttoken')
      try {
        const response = await axios.get(`${URL}tms/dashboard/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        console.log(response.data)
        setTotalTrainings(response.data.total_trainings)
        setTotalParticipants(response.data.total_participants)
        setApprovedTrainings(response.data.approved_trainings)
        setDeclinedTrainings(response.data.declined_trainings)
        setTrainingStatusData(response.data.training_status)
        setParticipantsData(response.data.training_participants)
        setMonthlyTrainingData(response.data.monthly_trainings)
        setRecentTrainings(response.data.recent_trainings)
      } catch (error) {
        console.error('Error fetching training list:', error)
      }
    }

    fetchDashboard()
  }, [])

  return (
    <div className='bg-light min-vh-100'style={{overflowX:"hidden",}}>
      <Container fluid>
        <div
          style={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: '#f0f4f8',
            fontFamily: 'Arial, sans-serif',
            
          }}
        >
          <TmsSidebar />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TmsHeader />
            <Row>
              <Col className='ms-sm-auto px-md-4'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom'>
                  <h1 className='h2'>Training Management Dashboard</h1>
                </div>

                <Row className='row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4'>
                  {stats.map((stat, index) => (
                    <Col key={index}>
                      <Card className={`bg-${stat.color} bg-opacity-10 h-100`}>
                        <Card.Body>
                          <div className='d-flex justify-content-between align-items-start'>
                            <div>
                              <div className='text-muted small'>
                                {stat.label}
                              </div>
                              <div className='fs-4 fw-bold'>{stat.value}</div>
                            </div>
                            <stat.icon
                              className={`text-${stat.color}`}
                              size={24}
                            />
                          </div>
                          <div
                            className={`small mt-2 text-${
                              stat.trend === 'up' ? 'success' : 'danger'
                            }`}
                          >
                            {stat.trend === 'up' ? (
                              <ArrowUp size={12} />
                            ) : (
                              <ArrowDown size={12} />
                            )}{' '}
                            {stat.percentage}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Card>
                      <Card.Body>
                        <Card.Title>Training Status Overview</Card.Title>
                        <div style={{ height: 300 }}>
                          <ResponsiveContainer width='100%' height='100%'>
                            <PieChart>
                              <Pie
                                data={trainingStatusData}
                                dataKey='value'
                                nameKey='status'
                                cx='50%'
                                cy='50%'
                                outerRadius={80}
                                fill='#8884d8'
                                label
                              >
                                {trainingStatusData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>

                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card>
                      <Card.Body>
                        <Card.Title>Top Trainings By Participants</Card.Title>
                        <div style={{ height: 300 }}>
                          <ResponsiveContainer width='100%' height='100%'>
                            <BarChart data={participantsData}>
                              <CartesianGrid strokeDasharray='3 3' />
                              <XAxis dataKey='training' />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey='value' fill='#8884d8' />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className='mb-4'>
                  <Card.Body>
                    <Card.Title>Monthly Training Trend</Card.Title>
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer width='100%' height='100%'>
                        <LineChart data={monthlyTrainingData}>
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis dataKey='month' />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type='monotone'
                            dataKey='value'
                            stroke='#8884d8'
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Body>
                    <Card.Title>Recent Trainings</Card.Title>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Training</th>
                          <th>Participants</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTrainings.map((training, index) => (
                          <tr key={index}>
                            <td>{training.training}</td>
                            <td>{training.participant}</td>
                            <td>
                              <Badge
                                bg={
                                  training.status === 'Approved'
                                    ? 'success'
                                    : training.status === 'Pending'
                                    ? 'warning'
                                    : 'primary'
                                }
                              >
                                {training.status}
                              </Badge>
                            </td>
                            <td>{training.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default TmsDashboard
